import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode, type WheelEvent } from 'react';
import { AlertTriangle, Flame, Lock, Minus, Plus, RefreshCcw, Zap } from 'lucide-react';
import { usePlant } from '../../state/PlantContext';
import { isolationApi, plantApi, ApiError } from '../../lib/api';
import type { IsolationPoint, PlantEdge, PlantNode } from '../../lib/types';
import { Spinner, ErrorBanner, EmptyState, DisclosureBanner } from '../../components/Misc';

/**
 * Spatial/topological plant map — closes the research's named "Spatial Risk Map / P&ID Topology
 * Viewer" gap. Renders the SAME plant_nodes/plant_edges data the deterministic conflict engine
 * reasons over (backend/src/domain/rules/isolationRules.ts) as an interactive SVG diagram, so an
 * operator can visually confirm why the engine said what it said — this is a read-only visualization
 * layer, it does not run any safety logic of its own and never overrides the server-side engine.
 */

const ISOLATION_STATE_COLOR: Record<IsolationPoint['currentState'], string> = {
  OPEN: 'var(--text-tertiary)',
  CLOSED: 'var(--status-advisory)',
  LOCKED: 'var(--accent)',
  VERIFIED: 'var(--status-clear)',
};

const NODE_SHAPE: Record<string, 'circle' | 'diamond' | 'rect' | 'triangle'> = {
  VESSEL: 'rect',
  PUMP: 'circle',
  COMPRESSOR: 'circle',
  TANK: 'rect',
  HEAT_EXCHANGER: 'rect',
  PIPING_SEGMENT: 'circle',
  GATE_VALVE: 'diamond',
  GLOBE_VALVE: 'diamond',
  BALL_VALVE: 'diamond',
  CHECK_VALVE: 'diamond',
  CONTROL_VALVE: 'diamond',
  SPECTACLE_BLIND: 'diamond',
  BREAKER: 'rect',
  MOTOR_CONTROL_CENTER: 'rect',
  INSTRUMENT: 'circle',
  FLARE_HEADER: 'circle',
  VENT_STACK: 'triangle',
  DRAIN_SUMP: 'triangle',
};

const NODE_RADIUS = 16;
const PADDING = 80;
const SCALE = 26; // world units (the same x/y the P&ID/DEXPI import writes) -> SVG pixels

interface IsolationOverlay {
  state: IsolationPoint['currentState'];
  tryoutConfirmed: boolean;
  certificateId: string;
}

export default function TopologyPage() {
  const { currentPlant } = usePlant();
  const [nodes, setNodes] = useState<PlantNode[]>([]);
  const [edges, setEdges] = useState<PlantEdge[]>([]);
  const [isolationByNode, setIsolationByNode] = useState<Record<string, IsolationOverlay>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const load = useCallback(async () => {
    if (!currentPlant) return;
    setError(null);
    try {
      const [topo, certRes] = await Promise.all([
        plantApi.topology(currentPlant.id),
        isolationApi.list(currentPlant.id),
      ]);
      setNodes(topo.nodes);
      setEdges(topo.edges);

      const overlay: Record<string, IsolationOverlay> = {};
      const activeCerts = certRes.certificates.filter((c) => c.status !== 'RELEASED');
      const pointLists = await Promise.all(activeCerts.map((c) => isolationApi.points(currentPlant.id, c.id)));
      pointLists.forEach((res, i) => {
        for (const point of res.points) {
          overlay[point.nodeId] = { state: point.currentState, tryoutConfirmed: point.tryoutConfirmed, certificateId: activeCerts[i]!.id };
        }
      });
      setIsolationByNode(overlay);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load plant topology.');
    } finally {
      setLoading(false);
    }
  }, [currentPlant]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const bounds = useMemo(() => {
    if (nodes.length === 0) return { minX: 0, minY: 0, maxX: 10, maxY: 10 };
    const xs = nodes.map((n) => n.x);
    const ys = nodes.map((n) => n.y);
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
  }, [nodes]);

  const project = useCallback(
    (n: PlantNode) => ({ x: (n.x - bounds.minX) * SCALE + PADDING, y: (n.y - bounds.minY) * SCALE + PADDING }),
    [bounds],
  );

  const viewW = (bounds.maxX - bounds.minX) * SCALE + PADDING * 2;
  const viewH = (bounds.maxY - bounds.minY) * SCALE + PADDING * 2;

  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const selected = selectedId ? nodeById.get(selectedId) ?? null : null;

  function resetView() {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }

  function onWheel(e: WheelEvent<SVGSVGElement>) {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom((z) => Math.min(3, Math.max(0.4, z + delta)));
  }

  function onPointerDown(e: PointerEvent<SVGSVGElement>) {
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    (e.target as Element).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: PointerEvent<SVGSVGElement>) {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan({ x: dragRef.current.panX + dx, y: dragRef.current.panY + dy });
  }
  function onPointerUp() {
    dragRef.current = null;
  }

  if (!currentPlant) return <Spinner label="Loading plant…" />;
  if (loading) return <Spinner label="Loading plant topology…" />;

  return (
    <div className="stack gap-5">
      <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="h2-section">Plant topology map</h1>
          <p className="body-text text-secondary">
            The same piping/electrical graph the isolation engine walks — {nodes.length} tagged nodes,{' '}
            {edges.length} connections.
          </p>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <DisclosureBanner>
        Read-only visualization. Node positions come from the imported topology (DEXPI drawing
        coordinates or manual layout) and do not necessarily reflect true plant GPS/GIS coordinates —
        see the DEXPI import warnings on the Isolation page. This view never runs safety logic; the
        server-side engine is the sole authority for any conflict determination.
      </DisclosureBanner>

      {nodes.length === 0 ? (
        <EmptyState
          icon={<AlertTriangle size={28} />}
          title="No topology imported for this plant"
          description="Import a DEXPI P&ID export or a native CSV/JSON topology file to populate this map."
        />
      ) : (
        <div className="topo-shell">
          <div className="topo-canvas-wrap">
            <div className="topo-toolbar">
              <button className="btn btn-secondary btn-icon" onClick={() => setZoom((z) => Math.min(3, z + 0.2))} aria-label="Zoom in">
                <Plus size={16} />
              </button>
              <button className="btn btn-secondary btn-icon" onClick={() => setZoom((z) => Math.max(0.4, z - 0.2))} aria-label="Zoom out">
                <Minus size={16} />
              </button>
              <button className="btn btn-secondary btn-icon" onClick={resetView} aria-label="Reset view">
                <RefreshCcw size={14} />
              </button>
            </div>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${viewW} ${viewH}`}
              className="topo-svg"
              onWheel={onWheel}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
                {edges.map((edge) => {
                  const from = nodeById.get(edge.fromNodeId);
                  const to = nodeById.get(edge.toNodeId);
                  if (!from || !to) return null;
                  const p1 = project(from);
                  const p2 = project(to);
                  const electrical = edge.edgeType === 'ELECTRICAL_ENERGIZES';
                  return (
                    <line
                      key={edge.id}
                      x1={p1.x}
                      y1={p1.y}
                      x2={p2.x}
                      y2={p2.y}
                      stroke={electrical ? 'var(--status-advisory)' : 'var(--border-strong)'}
                      strokeWidth={electrical ? 2 : 2.5}
                      strokeDasharray={electrical ? '5 4' : undefined}
                    />
                  );
                })}
                {nodes.map((node) => {
                  const p = project(node);
                  const shape = NODE_SHAPE[node.nodeType] ?? 'circle';
                  const iso = isolationByNode[node.id];
                  const isSelected = node.id === selectedId;
                  const fill = iso ? ISOLATION_STATE_COLOR[iso.state] : node.isEnergySource ? 'var(--status-critical)' : 'var(--surface)';
                  const stroke = isSelected ? 'var(--accent)' : node.isPositiveIsolation ? 'var(--accent)' : 'var(--border-strong)';
                  return (
                    <g
                      key={node.id}
                      transform={`translate(${p.x} ${p.y})`}
                      onClick={() => setSelectedId(node.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {shape === 'circle' && <circle r={NODE_RADIUS} fill={fill} stroke={stroke} strokeWidth={isSelected ? 3 : 2} />}
                      {shape === 'rect' && (
                        <rect x={-NODE_RADIUS} y={-NODE_RADIUS * 0.75} width={NODE_RADIUS * 2} height={NODE_RADIUS * 1.5} rx={5} fill={fill} stroke={stroke} strokeWidth={isSelected ? 3 : 2} />
                      )}
                      {shape === 'diamond' && (
                        <rect x={-NODE_RADIUS * 0.72} y={-NODE_RADIUS * 0.72} width={NODE_RADIUS * 1.44} height={NODE_RADIUS * 1.44} rx={3} fill={fill} stroke={stroke} strokeWidth={isSelected ? 3 : 2} transform="rotate(45)" />
                      )}
                      {shape === 'triangle' && <polygon points={`0,${-NODE_RADIUS} ${NODE_RADIUS},${NODE_RADIUS} ${-NODE_RADIUS},${NODE_RADIUS}`} fill={fill} stroke={stroke} strokeWidth={isSelected ? 3 : 2} />}
                      {node.flammable && (
                        <circle cx={NODE_RADIUS * 0.8} cy={-NODE_RADIUS * 0.8} r={5.5} fill="var(--status-advisory-bg)" stroke="var(--status-advisory)" strokeWidth={1.5} />
                      )}
                      <text y={NODE_RADIUS + 16} textAnchor="middle" fontSize={11} fontWeight={700} fill="var(--text)" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                        {node.tagNumber}
                      </text>
                    </g>
                  );
                })}
              </g>
            </svg>
          </div>

          <aside className="topo-panel">
            {selected ? (
              <NodeDetail node={selected} isolation={isolationByNode[selected.id]} onClose={() => setSelectedId(null)} />
            ) : (
              <TopoLegend />
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function TopoLegend() {
  return (
    <div className="stack gap-4">
      <div>
        <div className="eyebrow">Legend</div>
        <h3 className="h3" style={{ marginTop: 6 }}>Reading the map</h3>
      </div>
      <div className="stack gap-3">
        <LegendRow shape={<svg width={20} height={20}><circle cx={10} cy={10} r={8} fill="#fff" stroke="var(--border-strong)" strokeWidth={2} /></svg>} label="Equipment (pump, vessel, tank, instrument)" />
        <LegendRow shape={<svg width={20} height={20}><rect x={2} y={4} width={16} height={12} rx={3} fill="#fff" stroke="var(--border-strong)" strokeWidth={2} /></svg>} label="Vessel / tank / breaker / MCC" />
        <LegendRow shape={<svg width={20} height={20}><rect x={5} y={5} width={10} height={10} rx={2} fill="#fff" stroke="var(--accent)" strokeWidth={2} transform="rotate(45 10 10)" /></svg>} label="Valve (blue border = positive-isolation-capable)" />
        <LegendRow shape={<svg width={20} height={20}><polygon points="10,3 17,17 3,17" fill="#fff" stroke="var(--border-strong)" strokeWidth={2} /></svg>} label="Vent / drain sump" />
      </div>
      <div className="stack gap-2" style={{ paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        <div className="body-small" style={{ fontWeight: 700 }}>Isolation point fill color</div>
        {(['OPEN', 'CLOSED', 'LOCKED', 'VERIFIED'] as const).map((s) => (
          <div key={s} className="row gap-2" style={{ alignItems: 'center' }}>
            <span style={{ width: 12, height: 12, borderRadius: 4, background: ISOLATION_STATE_COLOR[s], display: 'inline-block' }} />
            <span className="body-small text-secondary">{s}</span>
          </div>
        ))}
      </div>
      <div className="row gap-2" style={{ alignItems: 'center', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        <Flame size={14} style={{ color: 'var(--status-advisory)' }} />
        <span className="body-small text-secondary">Small badge = flammable service</span>
      </div>
      <div className="row gap-2" style={{ alignItems: 'center' }}>
        <Zap size={14} style={{ color: 'var(--status-critical)' }} />
        <span className="body-small text-secondary">Solid red fill = hazardous energy source</span>
      </div>
      <p className="body-small text-tertiary" style={{ marginTop: 8 }}>
        Scroll to zoom, drag to pan, click any node for detail. Click the same node again elsewhere to
        deselect.
      </p>
    </div>
  );
}

function LegendRow({ shape, label }: { shape: ReactNode; label: string }) {
  return (
    <div className="row gap-3" style={{ alignItems: 'center' }}>
      {shape}
      <span className="body-small text-secondary">{label}</span>
    </div>
  );
}

function NodeDetail({ node, isolation, onClose }: { node: PlantNode; isolation?: IsolationOverlay; onClose: () => void }) {
  return (
    <div className="stack gap-4">
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="eyebrow">{node.nodeType.replace(/_/g, ' ')}</div>
          <h3 className="h3" style={{ marginTop: 4, fontFamily: "'IBM Plex Mono', monospace" }}>{node.tagNumber}</h3>
        </div>
        <div className="spacer" />
        <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">✕</button>
      </div>
      <p className="body-small text-secondary">{node.name}</p>

      <div className="stack gap-2">
        <DetailRow label="Fluid service" value={node.fluidService ?? '—'} />
        <DetailRow label="Operating pressure" value={node.operatingPressureKpa != null ? `${node.operatingPressureKpa} kPa` : '—'} />
        <DetailRow label="Elevation" value={`${node.elevationZMin} – ${node.elevationZMax} m`} />
      </div>

      <div className="stack gap-2" style={{ paddingTop: 8, borderTop: '1px solid var(--border)' }}>
        {node.flammable && <HazardBadge icon={<Flame size={13} />} label="Flammable service" tone="advisory" />}
        {node.toxic && <HazardBadge icon={<AlertTriangle size={13} />} label="Toxic service" tone="critical" />}
        {node.isEnergySource && <HazardBadge icon={<Zap size={13} />} label="Hazardous energy source" tone="critical" />}
        {node.isPositiveIsolation && <HazardBadge icon={<Lock size={13} />} label="Capable of positive isolation" tone="clear" />}
        {!node.flammable && !node.toxic && !node.isEnergySource && !node.isPositiveIsolation && (
          <span className="body-small text-tertiary">No hazard flags on this node.</span>
        )}
      </div>

      {isolation && (
        <div className="stack gap-2" style={{ paddingTop: 8, borderTop: '1px solid var(--border)' }}>
          <div className="body-small" style={{ fontWeight: 700 }}>Active isolation point</div>
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: ISOLATION_STATE_COLOR[isolation.state], display: 'inline-block' }} />
            <span className="body-small">{isolation.state}{isolation.tryoutConfirmed ? ' · try-out confirmed' : ''}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
      <span className="body-small text-tertiary">{label}</span>
      <span className="body-small" style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function HazardBadge({ icon, label, tone }: { icon: ReactNode; label: string; tone: 'advisory' | 'critical' | 'clear' }) {
  return (
    <div className="row gap-2" style={{ alignItems: 'center', color: `var(--status-${tone})` }}>
      {icon}
      <span className="body-small" style={{ fontWeight: 600 }}>{label}</span>
    </div>
  );
}
